import * as _ from 'lodash-es';
import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import type { QueryResult } from '@apollo/client';
import { useQuery } from '@apollo/client';
import type { VariablesOf } from '@graphql-typed-document-node/core';
import { useIsNonVisitorUser } from '../account/useIsLoggedIn';
import type { DocumentType } from '../graphql/generated/gql';
import { gql } from '../graphql/generated/gql';
import updateSubscribedList from '../utils/graphql/updateSubscribedList';
import useIsPublicView from '../utils/useIsPublicView';

// eslint-disable-next-line slab-graphql/no-unused-fields
gql(`
  fragment AllGroupFields on Group {
    id
    name
    description
    banner {
      original
    }
    allUsers {
      id
    }
    children {
      id
    }
  }
`);

const ALL_GROUPS_QUERY = gql(/** @complexity 14 */ `
  query allGroups {
    session: currentSession {
      organization {
        id
        groups {
          id
          ...AllGroupFields
        }
      }
    }
  }
`);

const GROUP_CREATED_SUBSCRIPTION = gql(/** @complexity 24 */ `
  subscription groupCreated {
    group: groupCreated {
      ...AllGroupFields
    }
  }
`);

const GROUP_DELETED_SUBSCRIPTION = gql(/** @complexity 4 */ `
  subscription groupDeleted {
    group: groupDeleted {
      id
    }
  }
`);

const GROUP_UPDATED_SUBSCRIPTION = gql(/** @complexity 24 */ `
  subscription groupUpdated {
    group: groupUpdated {
      ...AllGroupFields
    }
  }
`);

const useSubscribeEffect = (
  subscribeToMore: QueryResult<
    DocumentType<typeof ALL_GROUPS_QUERY>,
    VariablesOf<typeof ALL_GROUPS_QUERY>
  >['subscribeToMore'],
  skip: boolean,
) => {
  useEffect(() => {
    if (skip) return undefined;

    const unsubscribes = [
      GROUP_CREATED_SUBSCRIPTION,
      GROUP_DELETED_SUBSCRIPTION,
      GROUP_UPDATED_SUBSCRIPTION,
    ].map((query) => {
      const path = 'session.organization.groups';
      return subscribeToMore({
        document: query,
        updateQuery: updateSubscribedList(
          path,
          'group',
          query !== GROUP_DELETED_SUBSCRIPTION,
        ),
      });
    });

    return () => {
      unsubscribes.forEach((unsubscribe) => unsubscribe());
    };
  }, [skip, subscribeToMore]);
};

type UseAllGroupQueryOptions = {
  skip?: boolean;
};

const useAllGroupsQuery = (options?: UseAllGroupQueryOptions) => {
  const response = useQuery(ALL_GROUPS_QUERY, options);

  useSubscribeEffect(response.subscribeToMore, options?.skip ?? false);

  return useMemo(
    () => response?.data?.session.organization.groups ?? [],
    [response],
  );
};

type UseAllGroupOptions = {
  skip?: boolean;
};

const AllGroupsContext = createContext<{
  groups: ReturnType<typeof useAllGroupsQuery>;
  groupMap: Record<string, ReturnType<typeof useAllGroupsQuery>[number]>;
  query: () => void;
}>({ groups: [], groupMap: {}, query: () => {} });

const useAllGroups = (options?: UseAllGroupOptions) => {
  const { groups, query } = useContext(AllGroupsContext);

  useEffect(() => {
    if (!options?.skip) query();
  }, [options?.skip, query]);

  return groups;
};

export const useAllGroupMap = (options?: UseAllGroupOptions) => {
  const { groupMap, query } = useContext(AllGroupsContext);

  useEffect(() => {
    if (!options?.skip) query();
  }, [options?.skip, query]);

  return groupMap;
};

interface AllGroupsProviderProps {
  children: React.ReactNode;
}

export const AllGroupsProvider = ({ children }: AllGroupsProviderProps) => {
  const [isIdle, setIsIdle] = useState(true);

  const groups = useAllGroupsQuery({ skip: isIdle });

  const isNonVisitorUser = useIsNonVisitorUser();
  const isPublicView = useIsPublicView();

  const value = useMemo(
    () => ({
      query: () => setIsIdle(!isNonVisitorUser || isPublicView),
      groups,
      groupMap: _.keyBy(groups, 'id'),
    }),
    [isNonVisitorUser, isPublicView, groups],
  );

  return (
    <AllGroupsContext.Provider value={value}>
      {children}
    </AllGroupsContext.Provider>
  );
};

export default useAllGroups;
